// Mock data store for the Supplier Portal

import { useState, useEffect } from 'react'

export interface Truck {
  id: string
  plate: string
  type: string
  capacity: string
  status: 'Active' | 'In service' | 'Maintenance' | 'Idle'
  assignedDriverId?: string
  lastService: string
  location?: { lat: number; lng: number }
}

export interface Driver {
  id: string
  name: string
  phone: string
  license: string
  licenseExpiry: string
  assignedTruckId?: string
  status: 'On Duty' | 'Off Duty' | 'On Leave'
  rating: number
}

export interface Payment {
  id: string
  date: string
  jobId: string
  customer: string
  service: string
  amount: number
  status: 'Settled' | 'Pending' | 'Processing'
  settlementDate?: string
}

export type JobStatus =
  | 'unassigned'
  | 'pending_acceptance'
  | 'accepted'
  | 'in_progress'
  | 'at_pickup'
  | 'picked'
  | 'at_dropoff'
  | 'completed'

export interface Job {
  id: string
  sourceRequestId?: string
  serviceType: string
  jobDetails: JobDetails
  pickup: string
  dropoff: string
  scheduledDate: string
  startHour: number
  durationHours: number
  price: number
  assignedDriverId?: string
  assignedTruckId?: string
  customerName?: string
  customerPhone?: string
  photos?: string[]
  status: JobStatus
  // Timestamps (ms epoch) for each stage the job has reached.
  stageHistory?: Partial<Record<JobStatus, number>>
}

export type JobDetails =
  | {
      kind: 'car'
      make: string
      model: string
      year: number
      plate?: string
      condition?: string
    }
  | {
      kind: 'heavy-equipment'
      equipment: string
      weightTons: number
      dimensions?: string
      permitRequired?: boolean
    }
  | {
      kind: 'home-move'
      bedrooms: number
      floors?: number
      hasElevator?: boolean
      itemsNote?: string
    }
  | {
      kind: 'furniture'
      itemCount: number
      largestItem?: string
      assemblyRequired?: boolean
    }
  | {
      kind: 'recovery'
      vehicleType: string
      reason: string
      condition?: string
    }
  | {
      kind: 'refrigerated'
      cargoType: string
      weightKg: number
      tempRange: string
    }
  | { kind: 'general'; description: string }

export interface NegotiationEntry {
  from: 'customer' | 'supplier'
  amount: number
  at: string // human-readable timestamp e.g. "2 days ago" or "Today, 9:14 AM"
  note?: string
}

export interface SupplierRequest {
  id: string
  customerName: string
  serviceType: string
  pickup: string
  dropoff: string
  requestedTime: string
  // Pricing: originalPrice is the supplier's listed/auto-quoted price.
  // customerOffer (optional) is the discounted price the customer is counter-requesting.
  originalPrice: number
  customerOffer?: number
  // Legacy alias — keeps any existing reads working.
  suggestedPrice: number
  jobDetails: JobDetails
  status: 'pending' | 'awarded' | 'declined'
  type: 'direct' | 'bidding'
  // 'new' = brand-new incoming request from customer.
  // 'counter' = customer has come back with a counter-offer on an earlier round.
  // Undefined defaults to 'new' for backwards compatibility.
  subtype?: 'new' | 'counter'
  // Chronological history of price moves between supplier and customer for this job.
  // Only present on counter-offer requests.
  negotiation?: NegotiationEntry[]
  myBid?: number
  leadingBid?: number
  expiresIn?: string
  photos?: string[]
}

export type PricingGroupType = 'distance' | 'fixed' | 'city'
export type PricingTier = {
  id: string
  fromKm: number
  toKm: number | null
  price: number
}
export type CityPair = {
  id: string
  origin: string
  destination: string
  price: number
  bothWays: boolean
}
export interface PricingGroup {
  id: string
  name: string
  type: PricingGroupType
  baseFare?: number
  tiers?: PricingTier[]
  flatPrice?: number
  cityPairs?: CityPair[]
}

export interface AutoDispatchSettings {
  enabled: boolean
  weightDistance: number
  weightWorkload: number
  weightRating: number
  truckServiceMap: Record<string, string[]>
}

// --- MOCK DATA ---

export const MOCK_TRUCKS: Truck[] = [
  {
    id: 't1',
    plate: 'D 12345',
    type: '3-Ton Box Truck',
    capacity: '3 Tons',
    status: 'Active',
    assignedDriverId: 'd1',
    lastService: '2023-09-15',
    location: { lat: 25.2048, lng: 55.2708 },
  },
  {
    id: 't2',
    plate: 'A 98765',
    type: '1.5-Ton Pickup',
    capacity: '1.5 Tons',
    status: 'Active',
    assignedDriverId: 'd2',
    lastService: '2023-10-01',
    location: { lat: 25.1111, lng: 55.3333 },
  },
  {
    id: 't3',
    plate: 'E 45678',
    type: 'Flatbed 12m',
    capacity: '20 Tons',
    status: 'Idle',
    assignedDriverId: 'd3',
    lastService: '2023-08-20',
    location: { lat: 25.0, lng: 55.2 },
  },
  {
    id: 't4',
    plate: 'D 33333',
    type: 'Low-bed 40-Ton',
    capacity: '40 Tons',
    status: 'Maintenance',
    lastService: '2023-11-05',
  },
  {
    id: 't5',
    plate: 'S 55555',
    type: '5-Ton Curtain-side',
    capacity: '5 Tons',
    status: 'Active',
    assignedDriverId: 'd4',
    lastService: '2023-09-30',
    location: { lat: 25.3, lng: 55.4 },
  },
  {
    id: 't6',
    plate: 'A 11111',
    type: '3-Ton Box Truck',
    capacity: '3 Tons',
    status: 'Idle',
    assignedDriverId: 'd5',
    lastService: '2023-10-15',
    location: { lat: 24.9, lng: 55.1 },
  },
  {
    id: 't7',
    plate: 'D 77777',
    type: 'Flatbed recovery',
    capacity: '5 Tons',
    status: 'Active',
    assignedDriverId: 'd6',
    lastService: '2023-09-01',
    location: { lat: 25.25, lng: 55.3 },
  },
  {
    id: 't8',
    plate: 'E 88888',
    type: '1.5-Ton Pickup',
    capacity: '1.5 Tons',
    status: 'In service',
    assignedDriverId: 'd7',
    lastService: '2023-10-20',
    location: { lat: 25.15, lng: 55.25 },
  },
]

export const MOCK_DRIVERS: Driver[] = [
  {
    id: 'd1',
    name: 'Ahmed Khan',
    phone: '+971 50 123 4567',
    license: 'DL-987654',
    licenseExpiry: '2025-05-12',
    assignedTruckId: 't1',
    status: 'On Duty',
    rating: 4.8,
  },
  {
    id: 'd2',
    name: 'Mohammed Ali',
    phone: '+971 55 987 6543',
    license: 'DL-123456',
    licenseExpiry: '2023-11-30',
    assignedTruckId: 't2',
    status: 'On Duty',
    rating: 4.5,
  }, // Expiring soon
  {
    id: 'd3',
    name: 'Raju Sharma',
    phone: '+971 52 456 7890',
    license: 'DL-456123',
    licenseExpiry: '2026-01-15',
    status: 'Off Duty',
    rating: 4.9,
  },
  {
    id: 'd4',
    name: 'Tariq Mahmood',
    phone: '+971 56 333 4444',
    license: 'DL-789456',
    licenseExpiry: '2024-08-22',
    assignedTruckId: 't5',
    status: 'On Duty',
    rating: 4.7,
  },
  {
    id: 'd5',
    name: 'Usman Farooq',
    phone: '+971 50 555 6666',
    license: 'DL-321654',
    licenseExpiry: '2025-03-10',
    status: 'Off Duty',
    rating: 4.6,
  },
  {
    id: 'd6',
    name: 'Zayed Hassan',
    phone: '+971 54 777 8888',
    license: 'DL-654987',
    licenseExpiry: '2024-12-05',
    assignedTruckId: 't7',
    status: 'On Duty',
    rating: 4.9,
  },
  {
    id: 'd7',
    name: 'Imran Patel',
    phone: '+971 58 999 0000',
    license: 'DL-159753',
    licenseExpiry: '2026-07-18',
    assignedTruckId: 't8',
    status: 'On Duty',
    rating: 4.4,
  },
  {
    id: 'd8',
    name: 'Faisal Rehman',
    phone: '+971 50 222 3333',
    license: 'DL-357159',
    licenseExpiry: '2025-09-25',
    status: 'On Leave',
    rating: 4.8,
  },
  {
    id: 'd9',
    name: 'Bilal Qureshi',
    phone: '+971 55 444 1122',
    license: 'DL-741852',
    licenseExpiry: '2026-04-08',
    status: 'On Duty',
    rating: 4.5,
  },
  {
    id: 'd10',
    name: 'Yousef Al Mansoori',
    phone: '+971 56 211 8899',
    license: 'DL-963258',
    licenseExpiry: '2025-11-20',
    status: 'Off Duty',
    rating: 4.7,
  },
]

export const MOCK_PAYMENTS: Payment[] = [
  {
    id: 'PAY-1001',
    date: '2023-11-01',
    jobId: 'JOB-8472',
    customer: 'Sarah Jenkins',
    service: 'Home Move (2BR)',
    amount: 1250,
    status: 'Settled',
    settlementDate: '2023-11-03',
  },
  {
    id: 'PAY-1002',
    date: '2023-11-02',
    jobId: 'JOB-8475',
    customer: 'TechCorp LLC',
    service: 'B2B Pallet Delivery',
    amount: 850,
    status: 'Settled',
    settlementDate: '2023-11-04',
  },
  {
    id: 'PAY-1003',
    date: '2023-11-05',
    jobId: 'JOB-8480',
    customer: 'Michael Ross',
    service: 'Vehicle Recovery',
    amount: 350,
    status: 'Settled',
    settlementDate: '2023-11-07',
  },
  {
    id: 'PAY-1004',
    date: '2023-11-08',
    jobId: 'JOB-8485',
    customer: 'BuildRight Construction',
    service: 'Heavy Equipment Haul',
    amount: 2400,
    status: 'Processing',
  },
  {
    id: 'PAY-1005',
    date: '2023-11-09',
    jobId: 'JOB-8490',
    customer: 'Elena Rodriguez',
    service: 'Furniture Delivery',
    amount: 450,
    status: 'Pending',
  },
  {
    id: 'PAY-1006',
    date: '2023-11-09',
    jobId: 'JOB-8492',
    customer: 'FreshFoods Inc',
    service: 'Refrigerated Transport',
    amount: 1100,
    status: 'Pending',
  },
]

export const MOCK_REQUESTS: SupplierRequest[] = [
  {
    id: 'REQ-9001',
    customerName: 'David Chen',
    serviceType: 'Home Move (3BR)',
    pickup: 'Dubai Marina',
    dropoff: 'Jumeirah Golf Estates',
    requestedTime: 'Tomorrow, 9:00 AM',
    originalPrice: 1800,
    customerOffer: 1550,
    suggestedPrice: 1550,
    jobDetails: {
      kind: 'home-move',
      bedrooms: 3,
      floors: 2,
      hasElevator: true,
      itemsNote: 'Includes piano + 2 large wardrobes',
    },
    photos: [
      'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1505691938895-1758d7feb511?auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1493663284031-b7e3aefcae8e?auto=format&fit=crop&w=1200&q=80',
    ],
    status: 'pending',
    type: 'direct',
    subtype: 'counter',
    negotiation: [
      {
        from: 'customer',
        amount: 1400,
        at: '2 days ago',
        note: 'Initial budget',
      },
      {
        from: 'supplier',
        amount: 1700,
        at: '2 days ago',
        note: 'Includes piano + wardrobe handling and 2 floors',
      },
      {
        from: 'customer',
        amount: 1500,
        at: 'Yesterday',
        note: 'Can stretch a bit, but tight',
      },
      {
        from: 'supplier',
        amount: 1600,
        at: 'Yesterday',
        note: 'Final offer with 2 movers + dolly',
      },
      {
        from: 'customer',
        amount: 1550,
        at: 'Today, 8:14 AM',
        note: 'Meet in the middle — last counter from me',
      },
    ],
  },
  {
    id: 'REQ-9002',
    customerName: 'Al Futtaim Logistics',
    serviceType: 'Heavy Equipment',
    pickup: 'Jebel Ali Free Zone',
    dropoff: 'Abu Dhabi Industrial City',
    requestedTime: 'Nov 15, 6:00 AM',
    originalPrice: 3200,
    customerOffer: 2880,
    suggestedPrice: 2880,
    jobDetails: {
      kind: 'heavy-equipment',
      equipment: 'CAT 320 Excavator',
      weightTons: 22,
      dimensions: '9.5m × 2.8m × 3.1m',
      permitRequired: true,
    },
    photos: [
      'https://images.unsplash.com/photo-1581094794329-c8112a89af12?auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1530124566582-a618bc2615dc?auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1597149961419-cb5666ac1761?auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1494522855154-9297ac14b55f?auto=format&fit=crop&w=1200&q=80',
    ],
    status: 'pending',
    type: 'direct',
  },
  {
    id: 'REQ-9003',
    customerName: 'Emma Watson',
    serviceType: 'Furniture Delivery',
    pickup: 'IKEA Festival City',
    dropoff: 'Downtown Dubai',
    requestedTime: 'Today, 4:00 PM',
    originalPrice: 320,
    customerOffer: 250,
    suggestedPrice: 250,
    jobDetails: {
      kind: 'furniture',
      itemCount: 6,
      largestItem: 'PAX wardrobe (236cm)',
      assemblyRequired: true,
    },
    photos: [
      'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1506439773649-6e0eb8cfb237?auto=format&fit=crop&w=1200&q=80',
    ],
    status: 'pending',
    type: 'bidding',
    myBid: 280,
    leadingBid: 240,
    expiresIn: '2h 15m',
  },
  {
    id: 'REQ-9004',
    customerName: 'Oasis Supermarkets',
    serviceType: 'Refrigerated Transport',
    pickup: 'Al Aweer',
    dropoff: 'Sharjah City Center',
    requestedTime: 'Nov 12, 5:00 AM',
    originalPrice: 900,
    suggestedPrice: 900,
    jobDetails: {
      kind: 'refrigerated',
      cargoType: 'Fresh produce',
      weightKg: 1800,
      tempRange: '2°C – 6°C',
    },
    status: 'awarded',
    type: 'bidding',
    myBid: 850,
    leadingBid: 850,
  },
  {
    id: 'REQ-9005',
    customerName: 'James Wilson',
    serviceType: 'Vehicle Recovery',
    pickup: 'Sheikh Zayed Road',
    dropoff: 'Al Quoz Garage',
    requestedTime: 'ASAP',
    originalPrice: 350,
    customerOffer: 300,
    suggestedPrice: 300,
    jobDetails: {
      kind: 'recovery',
      vehicleType: 'Sedan — Toyota Camry 2021',
      reason: 'Engine breakdown',
      condition: 'Drivable wheels, needs flatbed',
    },
    status: 'declined',
    type: 'direct',
  },
  {
    id: 'REQ-9006',
    customerName: 'Layla Al Nuaimi',
    serviceType: 'Car Movement',
    pickup: 'Dubai Auto Gallery, Al Quoz',
    dropoff: 'Yas Marina Circuit, Abu Dhabi',
    requestedTime: 'Nov 14, 10:00 AM',
    originalPrice: 750,
    customerOffer: 600,
    suggestedPrice: 600,
    jobDetails: {
      kind: 'car',
      make: 'Porsche',
      model: '911 GT3',
      year: 2023,
      plate: 'AD 7',
      condition: 'Showroom — enclosed transport preferred',
    },
    photos: [
      'https://images.unsplash.com/photo-1611821064430-0d40291922d2?auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1503376780353-7e6692767b70?auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1544636331-e26879cd4d9b?auto=format&fit=crop&w=1200&q=80',
    ],
    status: 'pending',
    type: 'direct',
    subtype: 'counter',
    negotiation: [
      {
        from: 'customer',
        amount: 550,
        at: 'Yesterday, 11:00 AM',
        note: 'Quick quote request',
      },
      {
        from: 'supplier',
        amount: 700,
        at: 'Yesterday, 11:42 AM',
        note: 'Enclosed transport, insured AED 800k',
      },
      {
        from: 'customer',
        amount: 600,
        at: 'Today, 7:30 AM',
        note: 'Best I can do',
      },
    ],
  },
  {
    id: 'REQ-9007',
    customerName: 'Hassan Trading Co.',
    serviceType: 'Car Movement',
    pickup: 'Sharjah Port',
    dropoff: 'Al Aweer Used Car Market',
    requestedTime: 'Today, 7:00 PM',
    originalPrice: 480,
    customerOffer: 420,
    suggestedPrice: 420,
    jobDetails: {
      kind: 'car',
      make: 'Nissan',
      model: 'Patrol',
      year: 2019,
      condition: 'Non-running — winch required',
    },
    status: 'pending',
    type: 'direct',
  },
]

// Helper: build a timestamp `mins` minutes ago.
const minsAgo = (mins: number) => Date.now() - mins * 60 * 1000

export const MOCK_JOBS: Job[] = [
  {
    id: 'JOB-9101',
    serviceType: 'Home Move (3BR)',
    customerName: 'David Chen',
    customerPhone: '+971 50 234 8891',
    jobDetails: {
      kind: 'home-move',
      bedrooms: 3,
      floors: 2,
      hasElevator: true,
      itemsNote: 'Includes piano + 2 large wardrobes',
    },
    pickup: 'Dubai Marina',
    dropoff: 'Jumeirah Golf Estates',
    scheduledDate: 'today',
    startHour: 9,
    durationHours: 4,
    price: 1550,
    assignedDriverId: 'd1',
    assignedTruckId: 't1',
    status: 'at_pickup',
    photos: [
      'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1505691938895-1758d7feb511?auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1493663284031-b7e3aefcae8e?auto=format&fit=crop&w=1200&q=80',
    ],
    stageHistory: {
      pending_acceptance: minsAgo(220),
      accepted: minsAgo(180),
      in_progress: minsAgo(95),
      at_pickup: minsAgo(20),
    },
  },
  {
    id: 'JOB-9102',
    serviceType: 'Heavy Equipment',
    customerName: 'Al Futtaim Logistics',
    customerPhone: '+971 4 217 4444',
    jobDetails: {
      kind: 'heavy-equipment',
      equipment: 'CAT 320 Excavator',
      weightTons: 22,
      permitRequired: true,
    },
    pickup: 'Jebel Ali Free Zone',
    dropoff: 'Abu Dhabi Industrial City',
    scheduledDate: 'today',
    startHour: 14,
    durationHours: 3.5,
    price: 2880,
    assignedDriverId: 'd1',
    assignedTruckId: 't4',
    status: 'pending_acceptance',
    photos: [
      'https://images.unsplash.com/photo-1581094794329-c8112a89af12?auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1530124566582-a618bc2615dc?auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1597149961419-cb5666ac1761?auto=format&fit=crop&w=1200&q=80',
    ],
    stageHistory: {
      pending_acceptance: minsAgo(8),
    },
  },
  {
    id: 'JOB-9103',
    serviceType: 'Furniture Delivery',
    customerName: 'Emma Watson',
    customerPhone: '+971 55 661 2310',
    jobDetails: {
      kind: 'furniture',
      itemCount: 6,
      largestItem: 'PAX wardrobe (236cm)',
      assemblyRequired: true,
    },
    pickup: 'IKEA Festival City',
    dropoff: 'Downtown Dubai',
    scheduledDate: 'today',
    startHour: 10,
    durationHours: 2,
    price: 250,
    assignedDriverId: 'd2',
    assignedTruckId: 't2',
    status: 'completed',
    stageHistory: {
      pending_acceptance: minsAgo(420),
      accepted: minsAgo(380),
      in_progress: minsAgo(310),
      at_pickup: minsAgo(265),
      picked: minsAgo(225),
      at_dropoff: minsAgo(165),
      completed: minsAgo(140),
    },
  },
  {
    id: 'JOB-9104',
    serviceType: 'Car Movement',
    customerName: 'Layla Al Nuaimi',
    customerPhone: '+971 50 442 7780',
    jobDetails: {
      kind: 'car',
      make: 'Porsche',
      model: '911 GT3',
      year: 2023,
      condition: 'Showroom — enclosed transport preferred',
    },
    pickup: 'Dubai Auto Gallery, Al Quoz',
    dropoff: 'Yas Marina Circuit, Abu Dhabi',
    scheduledDate: 'tomorrow',
    startHour: 10,
    durationHours: 2.5,
    price: 600,
    photos: [
      'https://images.unsplash.com/photo-1611821064430-0d40291922d2?auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1503376780353-7e6692767b70?auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1544636331-e26879cd4d9b?auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1583121274602-3e2820c69888?auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1494976388531-d1058494cdd8?auto=format&fit=crop&w=1200&q=80',
    ],
    status: 'unassigned',
  },
  {
    id: 'JOB-9105',
    serviceType: 'Refrigerated Transport',
    customerName: 'Oasis Supermarkets',
    customerPhone: '+971 4 882 3411',
    jobDetails: {
      kind: 'refrigerated',
      cargoType: 'Fresh produce',
      weightKg: 1800,
      tempRange: '2°C – 6°C',
    },
    pickup: 'Al Aweer',
    dropoff: 'Sharjah City Center',
    scheduledDate: 'today',
    startHour: 16,
    durationHours: 2,
    price: 900,
    status: 'unassigned',
  },
  // --- Additional simulated jobs ---
  {
    id: 'JOB-9106',
    serviceType: 'Car Movement',
    customerName: 'Hassan Trading Co.',
    customerPhone: '+971 4 268 9012',
    jobDetails: {
      kind: 'car',
      make: 'Lexus',
      model: 'LX 600',
      year: 2024,
      plate: 'D 4421',
      condition: 'Pristine — daily driver',
    },
    pickup: 'Al Habtoor Motors, Dubai',
    dropoff: 'Mussafah Service Centre, Abu Dhabi',
    scheduledDate: 'today',
    startHour: 11,
    durationHours: 2.5,
    price: 480,
    assignedDriverId: 'd3',
    assignedTruckId: 't3',
    status: 'in_progress',
    stageHistory: {
      pending_acceptance: minsAgo(130),
      accepted: minsAgo(110),
      in_progress: minsAgo(35),
    },
  },
  {
    id: 'JOB-9107',
    serviceType: 'Recovery',
    customerName: 'James Wilson',
    customerPhone: '+971 56 778 4421',
    jobDetails: {
      kind: 'recovery',
      vehicleType: 'SUV — Nissan Patrol 2020',
      reason: 'Flat tyre, no spare',
      condition: 'Side of E11',
    },
    pickup: 'Sheikh Zayed Rd, Al Quoz exit',
    dropoff: 'Al Quoz Tyre Centre',
    scheduledDate: 'today',
    startHour: 12.5,
    durationHours: 1,
    price: 280,
    assignedDriverId: 'd6',
    assignedTruckId: 't7',
    status: 'picked',
    stageHistory: {
      pending_acceptance: minsAgo(85),
      accepted: minsAgo(75),
      in_progress: minsAgo(55),
      at_pickup: minsAgo(35),
      picked: minsAgo(15),
    },
  },
  {
    id: 'JOB-9108',
    serviceType: 'Furniture Delivery',
    customerName: 'Priya Sharma',
    customerPhone: '+971 52 113 9087',
    jobDetails: {
      kind: 'furniture',
      itemCount: 3,
      largestItem: '3-seater sofa',
      assemblyRequired: false,
    },
    photos: [
      'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1506439773649-6e0eb8cfb237?auto=format&fit=crop&w=1200&q=80',
    ],
    pickup: 'Home Centre, Mirdif',
    dropoff: 'JVC, Cluster 12',
    scheduledDate: 'today',
    startHour: 13,
    durationHours: 1.5,
    price: 220,
    assignedDriverId: 'd5',
    assignedTruckId: 't6',
    status: 'at_dropoff',
    stageHistory: {
      pending_acceptance: minsAgo(160),
      accepted: minsAgo(145),
      in_progress: minsAgo(115),
      at_pickup: minsAgo(90),
      picked: minsAgo(55),
      at_dropoff: minsAgo(10),
    },
  },
  {
    id: 'JOB-9109',
    serviceType: 'Home Move (1BR)',
    customerName: 'Marcus Bennett',
    customerPhone: '+971 50 998 1145',
    jobDetails: {
      kind: 'home-move',
      bedrooms: 1,
      floors: 1,
      hasElevator: true,
    },
    pickup: 'Discovery Gardens, Bldg 14',
    dropoff: 'The Greens, Al Sidir 4',
    scheduledDate: 'tomorrow',
    startHour: 8.5,
    durationHours: 3,
    price: 720,
    assignedDriverId: 'd7',
    assignedTruckId: 't8',
    status: 'pending_acceptance',
    stageHistory: {
      pending_acceptance: minsAgo(4),
    },
  },
  {
    id: 'JOB-9110',
    serviceType: 'Refrigerated Transport',
    customerName: 'FreshCo Distribution',
    customerPhone: '+971 4 555 6644',
    jobDetails: {
      kind: 'refrigerated',
      cargoType: 'Dairy + frozen meals',
      weightKg: 2400,
      tempRange: '-18°C – -10°C',
    },
    pickup: 'Al Quoz Cold Storage',
    dropoff: 'Carrefour MOE Distribution',
    scheduledDate: 'today',
    startHour: 6,
    durationHours: 2,
    price: 1300,
    assignedDriverId: 'd4',
    assignedTruckId: 't5',
    status: 'in_progress',
    stageHistory: {
      pending_acceptance: minsAgo(240),
      accepted: minsAgo(220),
      in_progress: minsAgo(165),
    },
  },
  {
    id: 'JOB-9111',
    serviceType: 'Heavy Equipment',
    customerName: 'BuildRight Construction',
    customerPhone: '+971 4 332 7700',
    jobDetails: {
      kind: 'heavy-equipment',
      equipment: 'JCB 3CX Backhoe',
      weightTons: 8,
      permitRequired: false,
    },
    pickup: 'Al Awir Equipment Yard',
    dropoff: 'Dubai South Construction Site',
    scheduledDate: 'tomorrow',
    startHour: 7,
    durationHours: 4,
    price: 1450,
    status: 'unassigned',
  },
  // --- Completed historical jobs (for driver schedule view) ---
  {
    id: 'JOB-9082',
    serviceType: 'Furniture Delivery',
    customerName: 'Noor Saleh',
    customerPhone: '+971 50 887 1234',
    jobDetails: {
      kind: 'furniture',
      items: 'Dining set + 6 chairs',
      floor: 3,
      hasElevator: true,
    },
    pickup: 'Home Centre, Mirdif',
    dropoff: 'JVC, District 14',
    scheduledDate: 'yesterday',
    startHour: 9,
    durationHours: 2,
    price: 320,
    assignedDriverId: 'd1',
    assignedTruckId: 't1',
    status: 'completed',
    stageHistory: {
      pending_acceptance: minsAgo(1500),
      accepted: minsAgo(1480),
      in_progress: minsAgo(1410),
      at_pickup: minsAgo(1380),
      picked: minsAgo(1340),
      at_dropoff: minsAgo(1280),
      completed: minsAgo(1260),
    },
  },
  {
    id: 'JOB-9085',
    serviceType: 'B2B Pallet Delivery',
    customerName: 'TechCorp LLC',
    customerPhone: '+971 4 555 1010',
    jobDetails: {
      kind: 'cargo',
      cargoType: 'Electronics pallets',
      pallets: 6,
      weightKg: 800,
    },
    pickup: 'Jebel Ali Free Zone',
    dropoff: 'Dubai Internet City',
    scheduledDate: 'yesterday',
    startHour: 11,
    durationHours: 3,
    price: 880,
    assignedDriverId: 'd4',
    assignedTruckId: 't5',
    status: 'completed',
    stageHistory: {
      pending_acceptance: minsAgo(1700),
      accepted: minsAgo(1680),
      in_progress: minsAgo(1620),
      at_pickup: minsAgo(1580),
      picked: minsAgo(1520),
      at_dropoff: minsAgo(1430),
      completed: minsAgo(1400),
    },
  },
  {
    id: 'JOB-9088',
    serviceType: 'Car Movement',
    customerName: 'Aisha Khalid',
    customerPhone: '+971 56 332 9988',
    jobDetails: { kind: 'car', make: 'BMW', model: 'X5', year: 2021 },
    pickup: 'AutoMart Sharjah',
    dropoff: 'Al Barsha, Dubai',
    scheduledDate: 'yesterday',
    startHour: 14,
    durationHours: 2,
    price: 420,
    assignedDriverId: 'd6',
    assignedTruckId: 't7',
    status: 'completed',
    stageHistory: {
      pending_acceptance: minsAgo(1900),
      accepted: minsAgo(1880),
      in_progress: minsAgo(1820),
      at_pickup: minsAgo(1780),
      picked: minsAgo(1720),
      at_dropoff: minsAgo(1650),
      completed: minsAgo(1620),
    },
  },
  {
    id: 'JOB-9091',
    serviceType: 'Home Move',
    customerName: 'David Chen',
    customerPhone: '+971 50 234 8891',
    jobDetails: { kind: 'home-move', bedrooms: 2, floor: 5, hasElevator: true },
    pickup: 'Dubai Marina',
    dropoff: 'Jumeirah Village Circle',
    scheduledDate: 'yesterday',
    startHour: 8,
    durationHours: 4,
    price: 1100,
    assignedDriverId: 'd1',
    assignedTruckId: 't1',
    status: 'completed',
    stageHistory: {
      pending_acceptance: minsAgo(2100),
      accepted: minsAgo(2070),
      in_progress: minsAgo(2010),
      at_pickup: minsAgo(1980),
      picked: minsAgo(1880),
      at_dropoff: minsAgo(1780),
      completed: minsAgo(1760),
    },
  },
  {
    id: 'JOB-9094',
    serviceType: 'Furniture Delivery',
    customerName: 'Marcus Bennett',
    customerPhone: '+971 50 998 1145',
    jobDetails: {
      kind: 'furniture',
      items: 'Sectional sofa',
      floor: 2,
      hasElevator: false,
    },
    pickup: 'IKEA Yas Mall',
    dropoff: 'Saadiyat Island, Abu Dhabi',
    scheduledDate: 'yesterday',
    startHour: 16,
    durationHours: 3,
    price: 540,
    assignedDriverId: 'd7',
    assignedTruckId: 't8',
    status: 'completed',
    stageHistory: {
      pending_acceptance: minsAgo(2300),
      accepted: minsAgo(2280),
      in_progress: minsAgo(2210),
      at_pickup: minsAgo(2170),
      picked: minsAgo(2100),
      at_dropoff: minsAgo(2010),
      completed: minsAgo(1990),
    },
  },
  {
    id: 'JOB-9096',
    serviceType: 'B2B Pallet Delivery',
    customerName: 'FreshCo Distribution',
    customerPhone: '+971 4 555 6644',
    jobDetails: {
      kind: 'cargo',
      cargoType: 'Refrigerated produce',
      pallets: 8,
      weightKg: 1200,
    },
    pickup: 'Al Aweer Fruit & Veg Market',
    dropoff: 'Carrefour Mall of Emirates',
    scheduledDate: 'yesterday',
    startHour: 6,
    durationHours: 2,
    price: 480,
    assignedDriverId: 'd4',
    assignedTruckId: 't5',
    status: 'completed',
    stageHistory: {
      pending_acceptance: minsAgo(2500),
      accepted: minsAgo(2490),
      in_progress: minsAgo(2440),
      at_pickup: minsAgo(2420),
      picked: minsAgo(2370),
      at_dropoff: minsAgo(2310),
      completed: minsAgo(2300),
    },
  },
]

// --- REACTIVE STORE ---

// Default services the carrier already covers (seed data) — aligned with fleet.
const DEFAULT_SERVICES = [
  'Apartment Move',
  'Office Move',
  'Vehicle Recovery',
  'Battery Boost',
]

let state = {
  requests: [...MOCK_REQUESTS],
  jobs: [...MOCK_JOBS],
  drivers: [...MOCK_DRIVERS],
  trucks: [...MOCK_TRUCKS],
  services: [...DEFAULT_SERVICES],
  pricingGroups: [] as PricingGroup[],
  serviceGroupMap: {} as Record<string, string>,
  autoDispatch: {
    enabled: false,
    weightDistance: 50,
    weightWorkload: 30,
    weightRating: 20,
    truckServiceMap: {},
  } as AutoDispatchSettings,
}

const listeners = new Set<() => void>()

function notify() {
  listeners.forEach((l) => l())
}

export function useSupplierStore() {
  const [, setTick] = useState(0)

  useEffect(() => {
    const h = () => setTick((t) => t + 1)
    listeners.add(h)
    return () => {
      listeners.delete(h)
    }
  }, [])

  const checkConflict = (
    driverId: string,
    date: string,
    startHour: number,
    durationHours: number,
  ): Job[] => {
    const endHour = startHour + durationHours
    return state.jobs.filter(
      (j) =>
        j.assignedDriverId === driverId &&
        j.scheduledDate === date &&
        ((startHour >= j.startHour &&
          startHour < j.startHour + j.durationHours) ||
          (endHour > j.startHour && endHour <= j.startHour + j.durationHours) ||
          (startHour <= j.startHour &&
            endHour >= j.startHour + j.durationHours)),
    )
  }

  return {
    ...state,
    acceptRequest: (requestId: string) => {
      const reqIndex = state.requests.findIndex((r) => r.id === requestId)
      if (reqIndex === -1) return null

      const req = state.requests[reqIndex]
      const newJob: Job = {
        id: `JOB-${Math.floor(Math.random() * 10000)}`,
        sourceRequestId: req.id,
        serviceType: req.serviceType,
        jobDetails: req.jobDetails,
        pickup: req.pickup,
        dropoff: req.dropoff,
        scheduledDate: 'today', // default for demo
        startHour: 12, // default for demo
        durationHours: 2, // default for demo
        price: req.customerOffer || req.originalPrice,
        customerName: req.customerName,
        customerPhone:
          '+971 50 ' +
          Math.floor(100 + Math.random() * 900) +
          ' ' +
          Math.floor(1000 + Math.random() * 9000),
        status: 'unassigned',
      }

      state.requests[reqIndex] = { ...req, status: 'awarded' }
      state.jobs = [newJob, ...state.jobs]
      notify()
      return newJob.id
    },
    declineRequest: (requestId: string) => {
      state.requests = state.requests.map((r) =>
        r.id === requestId ? { ...r, status: 'declined' } : r,
      )
      notify()
    },
    updateBid: (requestId: string, newBid: number) => {
      state.requests = state.requests.map((r) =>
        r.id === requestId ? { ...r, myBid: newBid } : r,
      )
      notify()
    },
    submitCounter: (requestId: string, amount: number, note?: string) => {
      state.requests = state.requests.map((r) => {
        if (r.id !== requestId) return r
        const now = new Date()
        const at = `Today, ${now.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        })}`
        const entry: NegotiationEntry = {
          from: 'supplier',
          amount,
          at,
          note,
        }
        return {
          ...r,
          subtype: 'counter',
          negotiation: [...(r.negotiation || []), entry],
        }
      })
      notify()
    },
    assignJob: (jobId: string, driverId: string, truckId: string) => {
      const job = state.jobs.find((j) => j.id === jobId)
      if (!job) return { conflict: [] }

      const conflicts = checkConflict(
        driverId,
        job.scheduledDate,
        job.startHour,
        job.durationHours,
      )
      if (conflicts.length > 0) {
        return { conflict: conflicts }
      }

      state.jobs = state.jobs.map((j) =>
        j.id === jobId
          ? {
              ...j,
              assignedDriverId: driverId,
              assignedTruckId: truckId,
              status: 'pending_acceptance',
              stageHistory: {
                ...(j.stageHistory || {}),
                pending_acceptance: Date.now(),
              },
            }
          : j,
      )
      notify()
      return { ok: true }
    },
    unassignJob: (jobId: string) => {
      state.jobs = state.jobs.map((j) =>
        j.id === jobId
          ? {
              ...j,
              assignedDriverId: undefined,
              assignedTruckId: undefined,
              status: 'unassigned',
            }
          : j,
      )
      notify()
    },
    reassignJob: (jobId: string, driverId: string, truckId: string) => {
      state.jobs = state.jobs.map((j) =>
        j.id === jobId
          ? {
              ...j,
              assignedDriverId: driverId,
              assignedTruckId: truckId,
              status: 'pending_acceptance',
              stageHistory: {
                ...(j.stageHistory || {}),
                pending_acceptance: Date.now(),
              },
            }
          : j,
      )
      notify()
    },
    updateJobStatus: (jobId: string, status: Job['status']) => {
      state.jobs = state.jobs.map((j) =>
        j.id === jobId
          ? {
              ...j,
              status,
              stageHistory: {
                ...(j.stageHistory || {}),
                [status]: Date.now(),
              },
            }
          : j,
      )
      notify()
    },
    assignTruckToDriver: (driverId: string, truckId: string | null) => {
      // Find driver's current truck (to unlink)
      const driver = state.drivers.find((d) => d.id === driverId)
      const previousTruckId = driver?.assignedTruckId
      state.drivers = state.drivers.map((d) => {
        if (d.id === driverId) {
          return { ...d, assignedTruckId: truckId || undefined }
        }
        // If another driver was holding this truck, clear it from them.
        if (truckId && d.assignedTruckId === truckId && d.id !== driverId) {
          return { ...d, assignedTruckId: undefined }
        }
        return d
      })
      state.trucks = state.trucks.map((t) => {
        // The newly assigned truck — point it at this driver.
        if (truckId && t.id === truckId) {
          return { ...t, assignedDriverId: driverId }
        }
        // The driver's previous truck — clear its driver link.
        if (previousTruckId && t.id === previousTruckId && t.id !== truckId) {
          return { ...t, assignedDriverId: undefined }
        }
        return t
      })
      notify()
    },
    addDriver: (input: {
      name: string
      phone: string
      license: string
      licenseExpiry: string
      status?: Driver['status']
      rating?: number
      truckId?: string
    }) => {
      const id = `d${Date.now().toString(36)}`
      const newDriver: Driver = {
        id,
        name: input.name,
        phone: input.phone,
        license: input.license,
        licenseExpiry: input.licenseExpiry,
        assignedTruckId: input.truckId,
        status: input.status || 'Off Duty',
        rating: input.rating ?? 5.0,
      }
      state.drivers = [...state.drivers, newDriver]
      if (input.truckId) {
        state.trucks = state.trucks.map((t) =>
          t.id === input.truckId ? { ...t, assignedDriverId: id } : t,
        )
      }
      notify()
      return id
    },
    getDriverSchedule: (driverId: string, date: string) => {
      return state.jobs
        .filter(
          (j) => j.assignedDriverId === driverId && j.scheduledDate === date,
        )
        .sort((a, b) => a.startHour - b.startHour)
    },
    updateJobSchedule: (
      jobId: string,
      startHour: number,
      durationHours?: number,
    ) => {
      state.jobs = state.jobs.map((j) =>
        j.id === jobId
          ? {
              ...j,
              startHour,
              ...(durationHours !== undefined ? { durationHours } : {}),
            }
          : j,
      )
      notify()
    },
    addService: (service: string) => {
      const normalized = service.trim()
      if (!normalized) return
      if (
        state.services.some((s) => s.toLowerCase() === normalized.toLowerCase())
      )
        return
      state.services = [...state.services, normalized]
      notify()
    },
    removeService: (service: string) => {
      state.services = state.services.filter((s) => s !== service)
      const newMap = { ...state.serviceGroupMap }
      delete newMap[service]
      state.serviceGroupMap = newMap
      notify()
    },
    addPricingGroup: (g: PricingGroup) => {
      state.pricingGroups = [...state.pricingGroups, g]
      notify()
    },
    updatePricingGroup: (id: string, g: Partial<PricingGroup>) => {
      state.pricingGroups = state.pricingGroups.map((group) =>
        group.id === id ? { ...group, ...g } : group,
      )
      notify()
    },
    deletePricingGroup: (id: string) => {
      state.pricingGroups = state.pricingGroups.filter((g) => g.id !== id)
      // Remove assignments
      const newMap = { ...state.serviceGroupMap }
      for (const [svc, gid] of Object.entries(newMap)) {
        if (gid === id) delete newMap[svc]
      }
      state.serviceGroupMap = newMap
      notify()
    },
    assignServiceGroup: (service: string, groupId: string | null) => {
      if (groupId) {
        state.serviceGroupMap = { ...state.serviceGroupMap, [service]: groupId }
      } else {
        const newMap = { ...state.serviceGroupMap }
        delete newMap[service]
        state.serviceGroupMap = newMap
      }
      notify()
    },
    updateAutoDispatch: (settings: Partial<AutoDispatchSettings>) => {
      state.autoDispatch = { ...state.autoDispatch, ...settings }
      notify()
    },
    addTruck: (input: Omit<Truck, 'id'>) => {
      const id = `t${Date.now().toString(36)}`
      const newTruck: Truck = { id, ...input }
      state.trucks = [...state.trucks, newTruck]
      if (input.assignedDriverId) {
        state.drivers = state.drivers.map((d) =>
          d.id === input.assignedDriverId ? { ...d, assignedTruckId: id } : d,
        )
      }
      notify()
      return id
    },
    updateTruck: (id: string, patch: Partial<Truck>) => {
      const prev = state.trucks.find((t) => t.id === id)
      state.trucks = state.trucks.map((t) =>
        t.id === id ? { ...t, ...patch } : t,
      )
      // Sync driver assignment if changed
      if (patch.assignedDriverId !== undefined) {
        const previousDriverId = prev?.assignedDriverId
        const newDriverId = patch.assignedDriverId
        state.drivers = state.drivers.map((d) => {
          if (d.id === previousDriverId && d.id !== newDriverId) {
            return { ...d, assignedTruckId: undefined }
          }
          if (d.id === newDriverId) {
            return { ...d, assignedTruckId: id }
          }
          return d
        })
      }
      notify()
    },
    deleteTruck: (id: string) => {
      const t = state.trucks.find((tr) => tr.id === id)
      state.trucks = state.trucks.filter((tr) => tr.id !== id)
      if (t?.assignedDriverId) {
        state.drivers = state.drivers.map((d) =>
          d.id === t.assignedDriverId
            ? { ...d, assignedTruckId: undefined }
            : d,
        )
      }
      // Unassign jobs that were on this truck
      state.jobs = state.jobs.map((j) =>
        j.assignedTruckId === id
          ? {
              ...j,
              assignedTruckId: undefined,
              assignedDriverId: undefined,
              status: 'unassigned',
            }
          : j,
      )
      notify()
    },
    checkConflict,
  }
}
